import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject, Optional } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// CONFIGS
import { CostAllocationTypeConstants } from '@app/core/_constant/settings.constants';
import { LocationService } from '@app/core/locations/services/location.service';
import { PurchaseOrderStatusInterface } from '@app/interfaces/purchase-order-status.model';
import { IIdName } from 'v2/modules/shared/interfaces/common.interface';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { formatDateToISOString } from 'v2/modules/shared/util/date.utils';
import {
  trackByControlValue,
  trackById,
} from 'v2/modules/shared/util/track-by.util';

import { PurchaseOrderFacadeService } from '../../purchase-order-facade.service';

import { SnackBarConfig } from './../../../shared/configs/snackbar.config';

// INTERFACES
import { atLeastOneArrayItemValidator } from './../../../shared/util/custom-validation.utils';
import { IPurchaseOrderShipment } from './../../interfaces/purchase-order.interface';

// SERVICES

// UTILS

//PIPES

// Constants

@Component({
  selector: 'lum-purchase-order-shipments-form',
  templateUrl: './purchase-order-shipments-form.component.html',
  styleUrls: ['./purchase-order-shipments-form.component.scss'],
})
export class PurchaseOrderShipmentsFormComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  warehouses :any[] = [];
  itemProductCategories :IIdName[] = [];


  trackById = trackById;
  trackByControlValue = trackByControlValue;

  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private purchaseOrderFacadeService :PurchaseOrderFacadeService,
    private snackbar :MatSnackBar,
    private datePipe :DatePipe,
    private route :ActivatedRoute,
    private router :Router,
    private locationSvc :LocationService,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private data :{
      id :number;
    },
    @Optional()
    private dialogRef :MatDialogRef<PurchaseOrderShipmentsFormComponent>,
    private decimalPlacesPipe :DecimalPlacesPipe,
  ) {}

  ngOnInit() :void {
    this.loadShipmentStatus();
    this.getWarehouses();

    if (this.currentShipmentId) {
      this.loadCurrentShipment();
    }

    this.initForm();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  getWarehouses() {
    this.locationSvc
      .getAll({ all: 1, parent_id: 0 })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp) => {
        this.warehouses = resp;
      });
  }

  onAddExtraCostItem(count :number) :void {
    for (let i = 0; i < count; i++) {
      this.extraCostsFormArray.push(
        this.fb.group({
          name: [''],
          quantity: [0],
          unitPrice: [0],
          costAllocationType: [
            CostAllocationTypeConstants.VALUE_BASED_ALLOCATION,
            [Validators.required],
          ],
          categories: this.fb.array([]),
        }),
      );
    }
  }

  onSearchPurchaseOrders(searchQuery :string) :void {
    this.purchaseOrderFacadeService
      .getPurchaseOrders({
        searchQuery: searchQuery || '',
        exact: 'true',
        relations:
          'supplierPurchaseOrderItems|purchaseOrderItems.rfq.unitOfMeasures|purchaseOrderItems.orderedUnitOfMeasure|factory',
        page: '1',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: (error) => {
          this.snackbar.open('Error loading purchase orders', 'Fail');
        },
      });
  }

  onSelectPurhaseOrder(purchaseOrder :any) :void {
    // Get the count of items that already existed in the form
    const existingItemsCount = this.itemsFormArray.controls.filter(
      (control :UntypedFormGroup) =>
        control.get('supplierPurchaseOrderId').value === purchaseOrder.id,
    ).length;

    purchaseOrder.orderItems.forEach((item :any) => {
      // Check if item already exists in the form
      if (
        this.itemsFormArray.controls.some(
          (control :UntypedFormGroup) =>
            control.get('supplierPurchaseOrderItemId').value === item.id,
        )
      ) {
        return;
      }
      const remainingShipmentQuantity = this.decimalPlacesPipe.transform(
        item?.remainingShipmentQuantity || 0,
      );
      this.itemsFormArray.push(
        this.fb.group({
          checked: [true],
          supplierPurchaseOrderId: [purchaseOrder.id],
          supplierPurchaseOrderItemId: [item.id],
          quantity: [remainingShipmentQuantity], // initially set the remaining shipment quantity,
          remainingShipmentQuantity: [remainingShipmentQuantity],
          unitOfMeasureId: [item?.orderedUomId],
          sku: [item?.rfq?.internalSku], // Not include in actual payload
          productName: [item?.rfq?.productName], // Not include in actual payload
          unitOfMeasures: [item?.rfq?.unitOfMeasures],
          productCategoryId: [item?.rfq?.productCategoryId],
          productCategory: [item?.rfq?.productCategory],
        }),
      );
    });

    this.#populateItemProductCategories(this.itemsFormArray?.value);

    if (existingItemsCount > 0) {
      this.snackbar.open(
        `${existingItemsCount} items already exist in the form`,
        'Warning',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );
    }
  }

  onRemovePurchaseOrder(purchaseOrder :any) :void {
    // Remove all items that belong to the purchase order
    this.itemsFormArray.controls = this.itemsFormArray.controls.filter(
      (control :UntypedFormGroup) =>
        control.get('supplierPurchaseOrderId').value !== purchaseOrder.id,
    );
  }

  displayFn(option :any) :string {
    return option && option.id ? `${option.id}` : '';
  }

  onCheckAllItems(event :MatCheckboxChange) :void {
    this.itemsFormArray.controls.forEach((control :UntypedFormGroup) => {
      control.get('checked').setValue(event.checked);
    });
  }

  onRemoveExtraCostItem(index :number) :void {
    this.extraCostsFormArray.removeAt(index);
  }

  onClose() :void {
    switch (this.mode) {
      case 'create':
      case 'update':
        this.dialogRef.close();
        break;
      case 'view':
        this.router.navigate(['orders']);
        break;
    }
  }

  onSubmit() :void {
    const { value } = this.form;

    let formattedShippingDate = null;
    if (value.shippingDate) {
      formattedShippingDate = formatDateToISOString(value.shippingDate);
    }

    const exceededItemQuantity = [];
    const payload = {
      referenceNumber: value.referenceNumber,
      shipmentStatusId: value.shipmentStatusId,
      shippingDate: formattedShippingDate,
      warehouseId: value.warehouseId || null,
      shipmentItems: value.shipmentItems
        .filter((item :any) => item.checked)
        .map((item :any) => {
          if (Number(item.quantity) > Number(item.remainingShipmentQuantity)) {
            exceededItemQuantity.push(item.sku);
          }

          return {
            id: item?.id || 0,
            supplierPurchaseOrderId: item.supplierPurchaseOrderId,
            supplierPurchaseOrderItemId: item.supplierPurchaseOrderItemId,
            quantity: item.quantity,
            unitOfMeasureId: item.unitOfMeasureId,
          };
        }),
      shipmentExtraCosts: value.shipmentExtraCosts.map((item :any) => ({
        id: item?.id || 0,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        costAllocationType: item.costAllocationType,
        categoryIds: item.categories.map((category :any) => category.id),
      })),
    };

    if (exceededItemQuantity?.length > 0) {
      this.snackbar.open(
        'Can not exceed remaining shipment quantity for: ' +
          exceededItemQuantity.join(', '),
        'Fail',
      );

      return;
    }

    if (!this.currentShipmentId) {
      this.createShipment(payload);
    } else {
      this.updateShipment(payload);
    }
  }

  #populateItemProductCategories(shipmentItems :any) :void {
    this.itemProductCategories = shipmentItems
      .filter((item) => item?.productCategoryId)
      .map((item) => ({ id: item?.productCategoryId, name: item.productCategory }))
      .filter(
        (value, index, self) => index === self.findIndex((t) => t.id === value.id),
      );
  }

  private createShipment(payload :any) :void {
    this.snackbar.open('Creating shipment...', 'Loading');
    this.purchaseOrderFacadeService
      .createShipment(payload)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.snackbar.dismiss();
          this.snackbar.open('Shipment created', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.dialogRef.close();
        },
        error: (response) => {
          this.snackbar.open(response?.error || 'Error creating shipment', 'Fail');
        },
      });
  }

  private updateShipment(payload :any) :void {
    this.snackbar.open('Updating shipment...', 'Loading');
    this.purchaseOrderFacadeService
      .updateShipment(this.currentShipmentId, payload)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.snackbar.dismiss();
          this.snackbar.open('Shipment updated', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.dialogRef.close();
        },
        error: (response) => {
          this.snackbar.open(response?.error || 'Error updating shipment', 'Fail');
        },
      });
  }

  private loadShipmentStatus() :void {
    this.purchaseOrderFacadeService
      .getShipmentStatus()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: (error) => {
          this.snackbar.open('Error loading shipment status', 'Fail');
        },
      });
  }

  private loadCurrentShipment() :void {
    this.purchaseOrderFacadeService
      .getShipmentById({
        id: this.currentShipmentId,
        params: {
          relations:
            'shipmentExtraCosts.categories.productCategory|shipmentItems.supplierPurchaseOrderItem|shipmentItems.supplierPurchaseOrderItem.rfq.unitOfMeasures|shipmentItems.unitOfMeasure|shipmentStatus',
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if(response?.shipmentItems) {
            const shipmentItems = response?.shipmentItems?.map((item :any) => item.supplierPurchaseOrderItem?.rfq ) || [];
            this.#populateItemProductCategories(shipmentItems);
          }
          this.initForm(response);
        },
        error: () => {
          this.snackbar.open('Error loading shipment', 'Fail');
        },
      });
  }

  private initForm(currentShipment ?:IPurchaseOrderShipment | null) :void {
    this.form = this.fb.group(
      {
        referenceNumber: [currentShipment?.referenceNumber || ''],
        shipmentStatusId: [currentShipment?.shipmentStatus?.id || ''],
        shippingDate: [
          currentShipment?.shippingDate
            ? this.datePipe.transform(currentShipment?.shippingDate, 'yyyy-MM-dd')
            : null,
        ],
        warehouseId: [currentShipment?.warehouseId || null],
        selectedPurchaseOrders: this.fb.array([]),
        shipmentItems: currentShipment?.shipmentItems
          ? this.fb.array(
              currentShipment.shipmentItems.map((item :any) =>
                this.fb.group({
                  checked: [true],
                  supplierPurchaseOrderId: [item.supplierPurchaseOrderId],
                  supplierPurchaseOrderItemId: [item.supplierPurchaseOrderItemId],
                  quantity: [item.quantity],
                  unitOfMeasureId: [item.unitOfMeasureId],
                  sku: [item?.supplierPurchaseOrderItem?.rfq?.internalSku],
                  productName: [item?.supplierPurchaseOrderItem?.rfq?.productName],
                  productCategoryId: [
                    item?.supplierPurchaseOrderItem?.rfq?.productCategoryId,
                  ],
                  productCategory: [
                    item?.supplierPurchaseOrderItem?.rfq?.productCategory,
                  ],
                  unitOfMeasures: [
                    item?.supplierPurchaseOrderItem?.rfq?.unitOfMeasures,
                  ],
                }),
              ),
            )
          : this.fb.array([]),
        shipmentExtraCosts: currentShipment?.shipmentExtraCosts
          ? this.fb.array(
              currentShipment.shipmentExtraCosts.map((item :any) =>
                this.fb.group({
                  name: [item.name, [Validators.required]],
                  quantity: [item.quantity],
                  unitPrice: [item.unitPrice],
                  costAllocationType: [
                    item.costAllocationType ||
                      CostAllocationTypeConstants.VALUE_BASED_ALLOCATION,
                    [Validators.required],
                  ],
                  categories: item?.categories
                    ? this.fb.array(
                        item.categories.map((category :any) =>
                          this.fb.group({
                            id: [category?.productCategory?.id],
                            name: [category?.productCategory?.name],
                          }),
                        ),
                      )
                    : this.fb.array([]),
                }),
              ),
            )
          : this.fb.array([]),
      },
      { validators: atLeastOneArrayItemValidator('shipmentItems') },
    );

    if (this.mode === 'view') {
      this.form.disable();
    }
  }

  get mode() :'create' | 'update' | 'view' {
    if (this.data?.id) {
      return 'update';
    } else if (this.paramId) {
      return 'view';
    } else {
      return 'create';
    }
  }

  get title() :string {
    switch (this.mode) {
      case 'create':
        return 'Create Shipment';
      case 'update':
        return 'Update Shipment';
      case 'view':
        return 'Shipment';
      default:
        return '';
    }
  }

  get headerIcon() :string {
    switch (this.mode) {
      case 'create':
      case 'update':
        return 'close';
      case 'view':
        return 'keyboard_backspace';
      default:
        return '';
    }
  }

  get paramId() :number | null {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  get currentShipmentId() :number | null {
    return this.data?.id || this.paramId;
  }

  get purchaseOrders$() :Observable<any[]> {
    return this.purchaseOrderFacadeService.purchaseOrders$;
  }

  get shipmentStatus$() :Observable<PurchaseOrderStatusInterface[]> {
    return this.purchaseOrderFacadeService.shipmentStatus$;
  }

  get loadingPurchaseOrders$() :Observable<boolean> {
    return this.purchaseOrderFacadeService.loadingPurchaseOrders$;
  }

  get submitting$() :Observable<boolean> {
    return this.purchaseOrderFacadeService.submitting$;
  }

  get loadingCurrentShipment$() :Observable<boolean> {
    return this.purchaseOrderFacadeService.loadingCurrentShipment$;
  }

  get currentShipment$() :Observable<IPurchaseOrderShipment> {
    return this.purchaseOrderFacadeService.currentShipment$;
  }

  get itemsFormArray() :UntypedFormArray {
    return this.form.get('shipmentItems') as UntypedFormArray;
  }

  get extraCostsFormArray() :UntypedFormArray {
    return this.form.get('shipmentExtraCosts') as UntypedFormArray;
  }

  get selectedPurchaseOrdersFormArray() :UntypedFormArray {
    return this.form.get('selectedPurchaseOrders') as UntypedFormArray;
  }

  get totalExtraCostQuantity() :number {
    return this.extraCostsFormArray.controls.reduce(
      (acc, control :UntypedFormGroup) => acc + control.get('quantity').value,
      0,
    );
  }

  get totalExtraCostUnitPrice() :number {
    return this.extraCostsFormArray.controls.reduce(
      (acc, control :UntypedFormGroup) => acc + control.get('unitPrice').value,
      0,
    );
  }

  get totalExtraCostLineTotal() :number {
    return this.extraCostsFormArray.controls.reduce(
      (acc, control :UntypedFormGroup) =>
        acc + control.get('quantity').value * control.get('unitPrice').value,
      0,
    );
  }

  get isItemsCheckedAll() :boolean {
    return this.itemsFormArray.controls.every(
      (control :UntypedFormGroup) => control.get('checked').value,
    );
  }

  get hasAtLeastOneItemChecked() :boolean {
    return this.itemsFormArray.controls.some(
      (control :UntypedFormGroup) => control.get('checked').value,
    );
  }
}
